import {
    DragDropContext,
    Draggable,
    DropResult,
    Droppable,
} from '@hello-pangea/dnd';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Supplier } from 'entities/Property/model/types/PropertySchema';

import { DragAndDropPlaceholder } from './DragAndDropPlaceholder';
import { SkeletonDragAndDrop } from './SkeletonDragAndDrop';
import { SupplierSourceRow } from './SupplierSourceRow';

type DragAndDropBlockProps = {
    preferredSuppliers: Supplier[] | [] | null;
    availableSuppliers: Supplier[] | [] | null;
    isLoadingSuppliers: boolean;
    setPreferredSuppliers: (arg0: [] | Supplier[]) => void;
    setAvailableSuppliers: (arg0: [] | Supplier[]) => void;
    applySettings: (
        arg0: {
            enablePreferredSuppliers?: boolean;
            preferredSuppliersOnly?: boolean;
        } | null,
        arg1: Supplier[] | null,
    ) => void;
    isSaveSettings: boolean;
    sortingModeLabel: string;
    showNumbers?: boolean;
    preferredSuppliersOnly?: boolean;
    saveInProgress?: boolean;
    autoSortByName?: boolean;
};

// a little function to help us with reordering the result
const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const getContainerStyle = (isDragging: boolean, draggableStyle: any) => ({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    ...draggableStyle,
});

const sortIfNeeded = (list: Supplier[], autoSortByName: boolean) => {
    if (autoSortByName) {
        list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
};

export const DragAndDropBlock = ({
    preferredSuppliers,
    availableSuppliers,
    isLoadingSuppliers,
    setPreferredSuppliers,
    setAvailableSuppliers,
    applySettings,
    isSaveSettings,
    sortingModeLabel,
    showNumbers = true,
    preferredSuppliersOnly = false,
    saveInProgress = false,
    autoSortByName = false,
}: DragAndDropBlockProps) => {
    const { t } = useTranslation();
    const prevAutoSortByNameRef = useRef(autoSortByName);
    const preferredSuppliersRef = useRef(preferredSuppliers);

    // Keep the ref updated with current preferredSuppliers
    useEffect(() => {
        preferredSuppliersRef.current = preferredSuppliers;
    }, [preferredSuppliers]);

    // Effect to re-sort preferred suppliers when autoSortByName changes
    useEffect(() => {
        // Only trigger if autoSortByName actually changed and we have suppliers
        if (
            prevAutoSortByNameRef.current !== autoSortByName &&
            preferredSuppliersRef.current &&
            preferredSuppliersRef.current.length > 0
        ) {
            const sortedSuppliers = sortIfNeeded(
                [...preferredSuppliersRef.current],
                autoSortByName,
            );
            setPreferredSuppliers(sortedSuppliers);
            applySettings(null, sortedSuppliers);
        }

        prevAutoSortByNameRef.current = autoSortByName;
    }, [autoSortByName, setPreferredSuppliers, applySettings]);

    const onDragEnd = useCallback(
        async (result: DropResult) => {
            // Disable drag and drop if save is in progress
            if (saveInProgress) {
                return;
            }

            const { source, destination } = result;

            // dropped outside the list
            if (!destination) {
                return;
            }

            if (source.droppableId === destination.droppableId) {
                const reorderedItems = reorder(
                    source.droppableId === 'available'
                        ? availableSuppliers
                        : preferredSuppliers,
                    source.index,
                    destination.index,
                );
                if (source.droppableId === 'available') {
                    setAvailableSuppliers(sortIfNeeded(reorderedItems, true));
                } else {
                    setPreferredSuppliers(
                        sortIfNeeded(reorderedItems, autoSortByName),
                    );
                    applySettings(null, reorderedItems);
                }
            } else {
                const result: any = {
                    available: [...availableSuppliers],
                    preferred: [...preferredSuppliers],
                };
                const [removed] = result[source.droppableId].splice(
                    source.index,
                    1,
                );
                result[destination.droppableId].splice(
                    destination.index,
                    0,
                    removed,
                );
                setAvailableSuppliers(sortIfNeeded(result.available, true));
                setPreferredSuppliers(
                    sortIfNeeded(result.preferred, autoSortByName),
                );
                applySettings(null, result.preferred);
            }
        },
        [
            applySettings,
            availableSuppliers,
            preferredSuppliers,
            setAvailableSuppliers,
            setPreferredSuppliers,
            saveInProgress,
            autoSortByName,
        ],
    );

    const showLoading =
        isLoadingSuppliers && !availableSuppliers && !preferredSuppliers;

    return (
        <>
            {showLoading ? (
                <SkeletonDragAndDrop />
            ) : (
                <>
                    <Box
                        data-testid="dnd-block"
                        sx={{
                            display: 'flex',
                            width: '100%',
                        }}
                    >
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Box
                                sx={{
                                    flex: 1,
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        marginBottom: '34px',
                                    }}
                                    data-testclass="dnd-section"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            flexDirection: 'row',
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '1.4rem',
                                                fontWeight: 700,
                                                lineHeight: '2rem',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {t('Preferred')}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: '1.3rem',
                                                lineHeight: '2rem',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {t('Sort by ')}
                                            <strong>{sortingModeLabel}</strong>
                                        </Typography>
                                    </Box>
                                    <Droppable droppableId="preferred">
                                        {(provided) => (
                                            <Box
                                                sx={{
                                                    marginTop: '16px',
                                                }}
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                            >
                                                {preferredSuppliers?.map(
                                                    (supplier, index) => (
                                                        <Draggable
                                                            key={supplier.id}
                                                            draggableId={
                                                                supplier.id
                                                            }
                                                            index={index}
                                                            isDragDisabled={
                                                                saveInProgress
                                                            }
                                                        >
                                                            {(
                                                                provided,
                                                                snapshot,
                                                            ) => (
                                                                <Box
                                                                    ref={
                                                                        provided.innerRef
                                                                    }
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={getContainerStyle(
                                                                        snapshot.isDragging,
                                                                        provided
                                                                            .draggableProps
                                                                            .style,
                                                                    )}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                            textAlign:
                                                                                'center',
                                                                            fontSize:
                                                                                '1.5rem',
                                                                            fontWeight: 700,
                                                                            lineHeight:
                                                                                '2.4rem',
                                                                            flexGrow: 0,
                                                                            marginTop:
                                                                                '-10px',
                                                                            padding:
                                                                                showNumbers
                                                                                    ? '0 13px 0 0'
                                                                                    : '0',
                                                                            maxWidth:
                                                                                showNumbers
                                                                                    ? '30px'
                                                                                    : '0px',
                                                                            transition:
                                                                                'all 0.2s ease-in-out',
                                                                            overflow:
                                                                                'hidden',
                                                                            boxSizing:
                                                                                'border-box',
                                                                        }}
                                                                    >
                                                                        {`${
                                                                            index +
                                                                            1
                                                                        }`}
                                                                    </Box>

                                                                    <SupplierSourceRow
                                                                        supplier={
                                                                            supplier
                                                                        }
                                                                        isDraggable={
                                                                            true
                                                                        }
                                                                        isDragging={
                                                                            snapshot.isDragging
                                                                        }
                                                                        isDragDisabled={
                                                                            saveInProgress
                                                                        }
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Draggable>
                                                    ),
                                                )}
                                                {preferredSuppliers !==
                                                    null && (
                                                    <DragAndDropPlaceholder />
                                                )}
                                                {provided.placeholder}
                                            </Box>
                                        )}
                                    </Droppable>
                                </Box>

                                <Box
                                    sx={{
                                        flex: 1,
                                    }}
                                    data-testclass="dnd-section"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            flexDirection: 'row',
                                            alignItems: 'flex-end',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '1.4rem',
                                                fontWeight: 700,
                                                lineHeight: '2rem',
                                                textTransform: 'uppercase',
                                                marginBottom: '16px',
                                            }}
                                        >
                                            {t('Other')}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: '1.3rem',
                                                lineHeight: '2rem',
                                                textAlign: 'right',
                                                marginBottom: '16px',
                                            }}
                                        >
                                            {t('Sort by ')}
                                            <strong>{t('Best price')}</strong>
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            overflow: 'hidden',
                                            transition: 'all 0.3s ease-in-out',
                                            maxHeight: preferredSuppliersOnly
                                                ? '100px'
                                                : '0px',
                                            opacity: preferredSuppliersOnly
                                                ? 1
                                                : 0,
                                            marginBottom: preferredSuppliersOnly
                                                ? '20px'
                                                : '0px',
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {t(
                                                'Only used if all preferred suppliers are out of stock:',
                                            )}
                                        </Typography>
                                    </Box>
                                    <Droppable droppableId="available">
                                        {(provided) => (
                                            <Box
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                            >
                                                {availableSuppliers?.map(
                                                    (supplier, index) => (
                                                        <Draggable
                                                            key={supplier.id}
                                                            draggableId={
                                                                supplier.id
                                                            }
                                                            index={index}
                                                            isDragDisabled={
                                                                saveInProgress
                                                            }
                                                        >
                                                            {(
                                                                provided,
                                                                snapshot,
                                                            ) => (
                                                                <Box
                                                                    ref={
                                                                        provided.innerRef
                                                                    }
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    style={getContainerStyle(
                                                                        snapshot.isDragging,
                                                                        provided
                                                                            .draggableProps
                                                                            .style,
                                                                    )}
                                                                >
                                                                    <SupplierSourceRow
                                                                        supplier={
                                                                            supplier
                                                                        }
                                                                        isDraggable={
                                                                            true
                                                                        }
                                                                        isNotUsed={
                                                                            preferredSuppliersOnly
                                                                        }
                                                                        isDragging={
                                                                            snapshot.isDragging
                                                                        }
                                                                        isDragDisabled={
                                                                            saveInProgress
                                                                        }
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Draggable>
                                                    ),
                                                )}
                                                {availableSuppliers !== null &&
                                                    availableSuppliers.length ===
                                                        0 && (
                                                        <DragAndDropPlaceholder />
                                                    )}
                                                {provided.placeholder}
                                            </Box>
                                        )}
                                    </Droppable>
                                </Box>
                            </Box>
                        </DragDropContext>
                    </Box>
                </>
            )}
        </>
    );
};
